import { Box, Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  NewResidenceVM,
  PracticeEditResidence,
  TableFamiliarVM,
  TableNewFamilySubjectsVM,
} from 'practice/view-models/practice-edit-residence';
import ModalFamily from '../modal-family';
import AccordionActualResidence from './accordion-actual-residence';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';
import ModalNewResidence from '../modal-new-residence';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { FormActualResidenceVM } from 'practice/view-models/pratice-actual-residence';

interface FormProps {
  praticaKey?: {
    cdEnte: string;
    tipoPratica: string;
    idAnnoPratica: number;
    pgPratica: number;
  } | null;
}

const FormComponent = ({ praticaKey }: FormProps): React.ReactElement => {
  const [modalFamily, setModalFamily] = useState({ open: false, isEdit: false });

  const [modalNewResidence, setModalNewResidence] = useState({ open: false, isEdit: false });

  const formContext = useFormikContext<PracticeEditResidence>();

  const onChangeTypeResidence = useEventCallback((value: string) => {
    // reset
    formContext.setValues({
      ...(formContext.values || {}),
      typeResidence: value,
      filter: undefined,
      tableFamiliar: undefined,
      tableNewFamilySubjects: undefined,
      filterDomesticPartnership: undefined,
      tableDomesticPartnership: undefined,
      newResidence: undefined,
      residence: undefined,
    });
  });

  const onConfirmNewResidence = useEventCallback((valueNew: NewResidenceVM) => {
    formContext.setFieldValue('residence', {
      checkedResidence: valueNew?.checkedResidence || false,
      italian:
        valueNew?.italian && !valueNew?.checkedResidence
          ? {
              ...valueNew?.italian,
              address: valueNew.italian.addressAuto || valueNew.italian.address || null,
            }
          : undefined,
      foreign: valueNew?.checkedResidence && valueNew?.foreign ? valueNew?.foreign : undefined,
      flagAttualeResidenza: valueNew.flagAttualeResidenza || false,
    });

    setModalNewResidence({ open: false, isEdit: modalNewResidence.isEdit });
  });

  const onEdit = useEventCallback(() => {
    switch (formContext.values.typeResidence) {
      case ResidenceID.allFamily:
      case ResidenceID.new:
        setModalNewResidence({ open: true, isEdit: true });
        return;
      case ResidenceID.family:
        setModalFamily({ open: true, isEdit: true });
        return;
      default:
        return;
    }
  });

  return (
    <Fragment>
      <Grid item container spacing={2}>
        <Grid item xs={12}>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Decorrenza *"
              aria-label="Decorrenza"
              value={formContext.values?.effectiveDate || null}
              onChange={(date) => {
                formContext.setFieldValue('effectiveDate', date || null);
              }}
              disabled={formContext.values.isFromData}
              renderInput={(params) => (
                <PmiTextField
                  id="effectiveDate"
                  {...params}
                  fullWidth
                  error={
                    formContext.touched.effectiveDate && Boolean(formContext.errors.effectiveDate)
                  }
                  helperText={formContext.errors.effectiveDate}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <StepButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            active={
              formContext.values.typeResidence === ResidenceID.new ||
              formContext.values.typeResidence === ResidenceID.allFamily
            }
            onClick={() => {
              if (
                formContext.values.typeResidence === ResidenceID.new ||
                formContext.values.typeResidence === ResidenceID.allFamily
              ) {
                onEdit();

                return;
              }

              onChangeTypeResidence(ResidenceID.new);
              // apertura modale
              setModalNewResidence({ open: true, isEdit: false });
            }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StepTypography
                active={
                  formContext.values.typeResidence === ResidenceID.new ||
                  formContext.values.typeResidence === ResidenceID.allFamily
                }
              >
                Nuova residenza
              </StepTypography>
            </Box>
          </StepButton>
        </Grid>

        <Grid item xs={12} md={4}>
          <StepButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            active={formContext.values.typeResidence === ResidenceID.family}
            onClick={() => {
              if (formContext.values.typeResidence === ResidenceID.family) {
                onEdit();

                return;
              }

              onChangeTypeResidence(ResidenceID.family);
              // apertura modale per ricerca famiglia
              setModalFamily({ open: true, isEdit: false });
            }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StepTypography active={formContext.values.typeResidence === ResidenceID.family}>
                Residenza in famiglia AIRE
              </StepTypography>
            </Box>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <AccordionActualResidence disabled onEdit={onEdit} />
        </Grid>
      </Grid>

      <ModalNewResidence
        open={modalNewResidence.open}
        onClose={() => {
          setModalNewResidence({ open: false, isEdit: modalNewResidence.isEdit });

          if (!modalNewResidence.isEdit) {
            onChangeTypeResidence('');
          }
        }}
        onConfirm={onConfirmNewResidence}
        typeResidence={formContext.values.typeResidence}
        codeEnte={praticaKey?.cdEnte}
        yearId={praticaKey?.idAnnoPratica?.toString()}
        typeRegister="IAIRE"
        progressive={praticaKey?.pgPratica?.toString()}
        initialValues={{
          ...(formContext.values.residence || {}),
          italian: {
            ...(formContext.values.residence?.italian || {}),
            addressAuto: formContext.values.residence?.italian?.addressAuto?.description
              ? formContext.values.residence?.italian?.addressAuto
              : formContext.values.residence?.italian?.address,
          },
        }}
      />
      <ModalFamily
        open={modalFamily.open}
        onClose={() => {
          setModalFamily({ open: false, isEdit: modalFamily.isEdit });

          if (!modalFamily.isEdit) {
            onChangeTypeResidence('');
          }
        }}
        onConfirm={(
          table?: TableNewFamilySubjectsVM,
          residence?: FormActualResidenceVM,
          tableFamiliar?: TableFamiliarVM
        ) => {
          setModalFamily({ open: false, isEdit: modalFamily.isEdit });

          formContext.setValues({
            ...formContext.values,
            tableFamiliar,
            tableNewFamilySubjects: table || undefined,
            residence: residence || formContext.values.residence || undefined,
          });
        }}
        initialValuesResidence={formContext.values.residence}
        initialValuesTableNewFamily={formContext.values.tableNewFamilySubjects}
        praticaKey={praticaKey}
      />
    </Fragment>
  );
};

FormComponent.displayName = 'Accordion Residence - form';

const Form = memo(FormComponent);

export default Form;
